import { useEffect, useRef, useState } from "react";

// Minimal fade-in-on-scroll utility used across the site.
// Adds a `.visible` class to any element with `.fade-in` when it enters the viewport.
export function useFadeInOnScroll() {
    useEffect(() => {
        const els = document.querySelectorAll(".fade-in");
        if (!("IntersectionObserver" in window)) {
            els.forEach((el) => el.classList.add("visible"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
}

// Wrapper component that adds fade-in class + optional stagger delay
export function FadeIn({ children, delay = 0, className = "", as: Tag = "div", ...rest }) {
    const style = delay ? { transitionDelay: `${delay}ms` } : undefined;
    return (
        <Tag className={`fade-in ${className}`} style={style} {...rest}>
            {children}
        </Tag>
    );
}

export function useScrolled(threshold = 30) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return scrolled;
}

export function useOnce(fn) {
    const called = useRef(false);
    useEffect(() => {
        if (!called.current) {
            called.current = true;
            fn();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
